'use client';

// Auto-slide carousel helper component
import React from 'react';

type AutoSlideCarouselProps = {
  carouselIndex: number;
  setCarouselIndex: React.Dispatch<React.SetStateAction<number>>;
  totalSlides: number;
  interval?: number;
};

const AutoSlideCarousel: React.FC<AutoSlideCarouselProps> = ({ carouselIndex, setCarouselIndex, totalSlides, interval = 5000 }) => {
  React.useEffect(() => {
    if (totalSlides <= 1) return;
    const timer = setInterval(() => {
      setCarouselIndex((prev: number) => (prev + 1) % totalSlides);
    }, interval);
    return () => clearInterval(timer);
  }, [totalSlides, interval, setCarouselIndex]);
  return null;
};

import { useEffect, useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { 
  Star, 
  ScrollText, 
  Sparkles, 
  Building2, 
  Plane, 
  Moon, 
  Gem, 
  Wand2, 
  Heart, 
  Briefcase, 
  HeartHandshake, 
  Leaf, 
  Brain, 
  CircleDot, 
  Crown, 
  Globe,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { apiService } from '@/lib/api';
import ServiceBookingForm from './ServiceBookingForm';

type IconComponent = typeof Star;

type ServiceCard = {
  id: number | string;
  icon: IconComponent;
  name: string;
  description: string;
  features: string[];
  href: string;
  category?: string;
  actionLabel?: string;
  extraDetails?: string;
  image?: string;
  price?: string;
  instant_rate?: number;
  scheduled_rate?: number;
  service_type?: 'instant' | 'scheduled';
  duration_minutes?: number;
  section_id?: number;
  section?: string;
  subsection?: string;
  formFields?: any[]; // FormField[] from ServiceFormBuilder
  serviceTypes?: any[]; // ServiceType[] from ServiceTypeManager
};

const OTHERS_SUBCATEGORY_KEY = '__others__';
const OTHERS_SUBCATEGORY_LABEL = 'Others';

const iconMap: Record<string, IconComponent> = {
  Star,
  ScrollText,
  Sparkles,
  Building2,
  Plane,
  Moon,
  Gem,
  Wand2,
  Heart,
  Briefcase,
  HeartHandshake,
  Leaf,
  Brain,
  CircleDot,
  Crown,
  Globe,
  Calendar,
  RefreshCw
};

const fallbackServices: ServiceCard[] = [];

// Helper function to clean service name if price is concatenated
const cleanServiceName = (name: string, price?: string): { cleanName: string; extractedPrice?: string } => {
  if (!name) return { cleanName: name || 'Service' };
  
  // If price is already provided separately, use it
  if (price) {
    return { cleanName: name.trim(), extractedPrice: price };
  }
  
  // Try to extract price from name if it looks concatenated (e.g., "Service60/hour" or "Service $60/hour")
  const pricePatterns = [
    /(.+?)(\d+\s*\/\s*(?:hour|appointment|session|consultation|visit|day|week|month))$/i,
    /(.+?)(\$?\s*\d+\s*\/\s*(?:hour|appointment|session|consultation|visit|day|week|month))$/i,
  ];
  
  for (const pattern of pricePatterns) {
    const match = name.match(pattern);
    if (match && match[1] && match[2]) {
      return {
        cleanName: match[1].trim(),
        extractedPrice: match[2].trim()
      };
    }
  }
  
  return { cleanName: name.trim() };
};

const isValidImageSrc = (src: any): string | undefined => {
  if (typeof src !== 'string') return undefined;
  const trimmed = src.trim();
  // Reject empty or "null"/"undefined" strings that would make next/image crash
  if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') return undefined;
  return trimmed;
};

const isSafeNextImageSrc = (src: string | undefined): string | undefined => {
  if (!src) return undefined;
  // Allow only same-origin or data URLs; external hosts can throw if not in next.config
  if (src.startsWith('/') || src.startsWith('data:')) return src;
  return undefined;
};

const normalizeService = (service: any, index: number): ServiceCard => {
  const iconName = (service.icon || 'Star') as keyof typeof iconMap;
  const IconComponent = iconMap[iconName] || Star;

  let features: string[] = [];
  if (Array.isArray(service.features)) {
    // Ensure all features are strings
    features = service.features.map((feature: any) => 
      typeof feature === 'string' ? feature : String(feature || '')
    );
  } else if (typeof service.features === 'string') {
    try {
      const parsed = JSON.parse(service.features);
      if (Array.isArray(parsed)) {
        // Ensure all parsed features are strings
        features = parsed.map((feature: any) => 
          typeof feature === 'string' ? feature : String(feature || '')
        );
      }
    } catch {
      features = [];
    }
  }

  // Clean service name if price is concatenated
  const priceFromApi = service.price || service.price_amount;
  const { cleanName, extractedPrice } = cleanServiceName(service.name || 'Service', priceFromApi);
  const finalPrice = priceFromApi || extractedPrice;

  // Parse formFields
  let formFields: any[] = [];
  if (service.form_fields) {
    if (Array.isArray(service.form_fields)) {
      formFields = service.form_fields;
    } else if (typeof service.form_fields === 'string') {
      try {
        formFields = JSON.parse(service.form_fields);
      } catch {
        formFields = [];
      }
    }
  }

  // Parse service_types
  let serviceTypes: any[] = [];
  if (service.service_types) {
    if (Array.isArray(service.service_types)) {
      serviceTypes = service.service_types;
    } else if (typeof service.service_types === 'string') {
      try {
        serviceTypes = JSON.parse(service.service_types);
      } catch {
        serviceTypes = [];
      }
    }
  }

  return {
    id: service.id || index,
    icon: IconComponent,
    name: cleanName,
    description: typeof service.description === 'string' ? service.description : String(service.description || ''),
    features,
    href: typeof service.href === 'string' ? service.href : String(service.href || '/'),
    category: typeof service.category === 'string' ? service.category : String(service.category || ''),
    actionLabel: typeof service.action_type === 'string' ? service.action_type : (service.action_type ? String(service.action_type) : undefined),
    extraDetails: typeof service.extra_details === 'string' ? service.extra_details : (service.extra_details ? String(service.extra_details) : undefined),
    image: isValidImageSrc(service.image),
    price: typeof finalPrice === 'string' ? finalPrice : (finalPrice ? String(finalPrice) : undefined),
    instant_rate: service.instant_rate ? Number(service.instant_rate) : undefined,
    scheduled_rate: service.scheduled_rate ? Number(service.scheduled_rate) : undefined,
    service_type: service.service_type && (service.service_type === 'instant' || service.service_type === 'scheduled') 
      ? service.service_type 
      : undefined,
    duration_minutes: service.duration_minutes ? Number(service.duration_minutes) : undefined,
    section_id: service.section_id ? Number(service.section_id) : undefined,
    section: typeof service.section === 'string' ? service.section : (service.section ? String(service.section) : undefined),
    subsection: typeof service.subsection === 'string' ? service.subsection : (service.subsection ? String(service.subsection) : undefined),
    formFields: formFields,
    serviceTypes: serviceTypes
  };
};

const normalizeServicesArray = (arr: any[]): ServiceCard[] => {
  if (!Array.isArray(arr)) return [];
  return arr.map((service, idx) => normalizeService(service, idx));
};

const isFallbackList = (list: ServiceCard[]) => {
  if (fallbackServices.length === 0) return false;
  if (list.length !== fallbackServices.length) return false;
  return list.every((item, idx) => {
    const fb = fallbackServices[idx];
    return item.name === fb.name && item.id === fb.id;
  });
};

interface ServicesSectionProps {
  simplified?: boolean; // If true, show only name and icon (for homepage)
  selectedServiceId?: number | string; // Service ID to auto-open modal (for services page)
  disableTopPadding?: boolean; // If true, outer section controls vertical spacing
  categoryFilter?: string; // Filter services by category name
}

export default function ServicesSection({ simplified = false, selectedServiceId, disableTopPadding = false, categoryFilter }: ServicesSectionProps) {
  // Cache key for localStorage
  const CACHE_KEY = 'swastishanti_services_cache';
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
  
  // Load from cache first for instant display - use lazy initialization
  const loadFromCache = (): ServiceCard[] | null => {
    if (typeof window === 'undefined') return null;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.data && Array.isArray(parsed.data)) {
          const { data, timestamp } = parsed;
          if (Date.now() - timestamp < CACHE_EXPIRY) {
            // Validate cached data structure
            if (Array.isArray(data) && data.length > 0) {
              // Normalize cached data to avoid rendering non-string/object values
              const normalized = normalizeServicesArray(data);
              return normalized;
            }
          }
        }
      }
    } catch (e) {
      // Clear corrupted cache
      try {
        if (typeof window !== 'undefined') {
          localStorage.removeItem(CACHE_KEY);
        }
      } catch {
        // Ignore cleanup errors
      }
    }
    return null;
  };
  
  // Save to cache
  const saveToCache = (data: ServiceCard[]) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      // Ignore cache errors
    }
  };
  
  // Use lazy initialization for state to safely load from cache
  const [services, setServices] = useState<ServiceCard[]>(() => {
    try {
      const cached = loadFromCache();
      return cached || fallbackServices;
    } catch (error) {
      console.error('Error loading services from cache:', error);
      return [];
    }
  });
  const [usingFallback, setUsingFallback] = useState<boolean>(() => {
    try {
      const cached = loadFromCache();
      if (cached && cached.length) {
        return isFallbackList(cached);
      }
    } catch {
      // ignore
    }
    return true;
  });
  const [loading, setLoading] = useState(false); // Start as false - show content immediately
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState<number | null>(null);
  const [selectedServiceData, setSelectedServiceData] = useState<ServiceCard | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeError, setIframeError] = useState(false);
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const [isModalTransitioning, setIsModalTransitioning] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0); // Carousel state for homepage
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const router = useRouter();

  // Update the URL query without triggering a Next.js navigation/re-render
  const replaceQuerySilently = useCallback((serviceId?: number | string) => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      if (serviceId !== undefined && serviceId !== null) {
        url.searchParams.set('id', String(serviceId));
      } else {
        url.searchParams.delete('id');
      }
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.error('❌ Error updating URL silently:', error);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchServices = async () => {
      try {
        console.log('🔍 Fetching services...');
        
        // Call API directly - it has its own timeout handling
        const response: any = await apiService.getServices(true);
        
        console.log('📥 Services response:', response);
        
        if (mounted) {
          let servicesToSet: ServiceCard[] = [];
          
          // Check if response has valid data
          if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
            console.log(`✅ Found ${response.data.length} services from API`);
            const normalized = normalizeServicesArray(response.data);
            servicesToSet = normalized;
            setError('');
          } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
            console.log(`✅ Found ${response.data.length} services (no success flag)`);
            const normalized = normalizeServicesArray(response.data);
            servicesToSet = normalized;
          } else {
            console.log('⚠️ No services in response, using fallback');
          }
          
          // Always set services - use fallback if API didn't return any
          setServices(servicesToSet);
          setUsingFallback(isFallbackList(servicesToSet));
          // Cache the services for instant loading next time
          saveToCache(servicesToSet);
          console.log(`✅ Set ${servicesToSet.length} services to display`);
        }
      } catch (err: any) {
        if (!mounted) return;
        const errorMsg = typeof err === 'object' && err !== null && 'message' in err 
          ? String(err.message || 'Unknown error')
          : String(err || 'Unknown error');
        console.warn('⚠️ Error fetching services (using fallback):', errorMsg);
        // Keep fallback services - don't show error to user
        setServices([]);
        setUsingFallback(true);
        setError('');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Fetch in background - don't block initial render
    fetchServices();
    return () => {
      mounted = false;
    };
  }, []);

  // List of valid routes that exist and can be loaded in iframe
  const validIframeRoutes = [
    '/appointments',
    '/find-guru',
    '/date-converter',
    '/panchanga',
    '/profile'
  ];

  // List of valid routes that can be navigated to (includes iframe routes + other valid pages)
  const validNavigationRoutes = [
    '/appointments',
    '/find-guru',
    '/date-converter',
    '/panchanga',
    '/profile',
    '/services',
    '/blog',
    '/about-us'
  ];

const shouldRouteToDateConverter = (service: ServiceCard) => {
    const href = (service.href || '').toLowerCase();
    const name = (service.name || '').toLowerCase();
    return (
      service.id === 7 ||
      href.includes('date-converter') ||
      name.includes('date converter')
    );
  };

const shouldRouteToCalendar = (service: ServiceCard) => {
  const href = (service.href || '').toLowerCase();
  const name = (service.name || '').toLowerCase();
  return (
    service.id === 2 ||
    href.includes('calendar') ||
    name.includes('calendar')
  );
};

  const handleServiceClick = (index: number, service: ServiceCard, e: React.MouseEvent) => {
    try {
      e.preventDefault();
      e.stopPropagation();
      
      // Validate service data - allow services without ID (use index as fallback)
      if (!service) {
        console.warn('⚠️ Invalid service data:', service);
        return;
      }
      
      // Prevent any navigation to /services/* routes - they should only open modals
      if (service.href && service.href.startsWith('/services/')) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      // Create a deep copy of the service FIRST to ensure data is ready
      // Use index as fallback ID if service.id is missing
      const serviceCopy: ServiceCard = {
        id: service.id || index + 1,
        icon: service.icon,
        name: service.name || 'Service',
        description: service.description || '',
        features: Array.isArray(service.features) ? [...service.features] : [],
        href: service.href || '/',
        category: service.category || '',
        actionLabel: service.actionLabel,
        extraDetails: service.extraDetails,
        image: service.image,
        price: service.price,
        instant_rate: service.instant_rate,
        scheduled_rate: service.scheduled_rate,
        service_type: service.service_type,
        duration_minutes: service.duration_minutes,
        formFields: service.formFields,
        serviceTypes: service.serviceTypes
      };
      
      // If simplified mode (homepage), navigate to services page with service ID
      if (simplified) {
        // If we're showing fallback data, don't pass an ID that won't exist on the Services page
        const serviceId = service.id || index;
        try {
          if (usingFallback) {
            router.push('/services');
          } else {
            router.push(`/services?id=${serviceId}`);
          }
        } catch (navError) {
          console.error('❌ Navigation error:', navError);
          // Fallback: try to open modal directly instead of navigating
          setSelectedServiceData(serviceCopy);
          setShowModal(index);
        }
        return;
      }
    
    // Only route to specific pages for Date Converter and Calendar
    if (shouldRouteToDateConverter(service)) {
      const targetHref = service.href && service.href !== '/' ? service.href : '/date-converter';
      router.push(targetHref);
      return;
    }
    
    if (shouldRouteToCalendar(service)) {
      const targetHref = service.href && service.href !== '/' ? service.href : '/#calendar';
      router.push(targetHref);
      return;
    }
    
      // For ALL other services, open modal popup (never route)
      // Batch all state updates together to prevent visible re-renders
      // Use requestAnimationFrame to ensure smooth transition
      requestAnimationFrame(() => {
        try {
          // Set all state in one batch - prevents visible flash
          setSelectedServiceData(serviceCopy);
          setIframeError(false);
          
          if (service.href && 
              service.href !== '/' && 
              validIframeRoutes.includes(service.href)) {
            setIframeUrl(service.href);
          } else {
            setIframeUrl(null);
          }
          
          setShowModal(index);
          
          // Update URL asynchronously after modal state is set (prevents visible flash)
          const serviceId = service.id || index;
          // Use router.replace to properly update URL (only if not in simplified mode)
          if (!simplified) {
            try {
              replaceQuerySilently(serviceId);
            } catch (urlError) {
              console.error('❌ URL update error:', urlError);
              // Continue without URL update
            }
          }
        } catch (stateError) {
          console.error('❌ Error setting modal state:', stateError);
          // Don't throw - prevent error boundary from catching this
        }
      });
    } catch (error) {
      console.error('❌ Error in handleServiceClick:', error);
      // Don't throw - prevent error boundary from catching this
    }
  };

  const closeModal = useCallback(() => {
    // Mark as manually closed to prevent auto-reopening
    setManuallyClosed(true);
    
    // Start transition - fade out modal
    setIsModalTransitioning(true);
    
    // Wait for transition to complete before removing from DOM
    setTimeout(() => {
      setShowModal(null);
      setSelectedServiceData(null);
      setIframeUrl(null);
      setIframeError(false);
      setIsModalTransitioning(false);
      
      // Remove query param without triggering page re-render
      replaceQuerySilently();
    }, 200); // Match CSS transition duration
  }, [replaceQuerySilently]);

  const handleIframeError = () => {
    setIframeError(true);
    setIframeUrl(null);
  };

  // Use stored service data, with fallback to index lookup
  // Add error handling to prevent crashes
  const selectedService = useMemo(() => {
    try {
      if (selectedServiceData) {
        return selectedServiceData;
      }
      if (showModal !== null && typeof showModal === 'number' && showModal >= 0 && showModal < services.length) {
        return services[showModal] || null;
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting selected service:', error);
      return null;
    }
  }, [selectedServiceData, showModal, services]);
  
  // Filter services by category if categoryFilter is provided
  const normalizedCategoryFilter = useMemo(
    () => (categoryFilter ? categoryFilter.trim().toLowerCase() : ''),
    [categoryFilter]
  );

  const filteredServices = useMemo(() => {
    if (!normalizedCategoryFilter) return services;
    return services.filter(service => {
      const serviceCategory = typeof service.category === 'string' ? service.category : String(service.category || '');
      return serviceCategory.trim().toLowerCase() === normalizedCategoryFilter;
    });
  }, [services, normalizedCategoryFilter]);

  const visibleServices = useMemo(() => {
    if (filteredServices.length > 0) return filteredServices;
    if (normalizedCategoryFilter) {
      if (usingFallback) {
        return services.length > 0 ? services : [];
      }
      return [];
    }
    return services.length > 0 ? services : [];
  }, [filteredServices, normalizedCategoryFilter, usingFallback, services]);

  const getSubcategoryKey = useCallback((service: ServiceCard) => {
    const sectionName = (service.section || service.subsection || '').trim();
    return sectionName || OTHERS_SUBCATEGORY_KEY;
  }, []);

  const subcategoryGroups = useMemo(() => {
    if (!normalizedCategoryFilter) return [];

    const groupsMap = new Map<string, { key: string; label: string; count: number }>();

    visibleServices.forEach((service) => {
      const key = getSubcategoryKey(service);
      const existing = groupsMap.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      groupsMap.set(key, {
        key,
        label: key === OTHERS_SUBCATEGORY_KEY ? OTHERS_SUBCATEGORY_LABEL : key,
        count: 1
      });
    });

    return Array.from(groupsMap.values()).sort((a, b) => {
      if (a.key === OTHERS_SUBCATEGORY_KEY) return 1;
      if (b.key === OTHERS_SUBCATEGORY_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [visibleServices, normalizedCategoryFilter, getSubcategoryKey]);

  useEffect(() => {
    if (!normalizedCategoryFilter) {
      setSelectedSubcategory('');
      return;
    }

    if (subcategoryGroups.length === 0) {
      setSelectedSubcategory('');
      return;
    }

    const hasSelectedGroup = subcategoryGroups.some((group) => group.key === selectedSubcategory);
    if (!hasSelectedGroup) {
      setSelectedSubcategory(subcategoryGroups[0].key);
    }
  }, [normalizedCategoryFilter, subcategoryGroups, selectedSubcategory]);

  const displayedServices = useMemo(() => {
    if (!normalizedCategoryFilter) {
      return visibleServices;
    }

    if (!selectedSubcategory) {
      return [];
    }

    return visibleServices.filter((service) => getSubcategoryKey(service) === selectedSubcategory);
  }, [visibleServices, normalizedCategoryFilter, selectedSubcategory, getSubcategoryKey]);

  // Memoize service cards to prevent re-render when modal opens/closes
  const mobileServiceCards = useMemo(() => 
    displayedServices.map((service, index) => (
      <button
        key={index}
        type="button"
        onClick={(e) => handleServiceClick(index, service, e)}
        className="group bg-white dark:bg-dark-800 rounded-lg p-3 sm:p-4 shadow-brand-sm hover:shadow-brand-md transition-all duration-200 border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:-translate-y-0.5 flex flex-col text-left cursor-pointer h-full"
      >
        {isSafeNextImageSrc(service.image) ? (
          <div className="relative mb-2 sm:mb-3 w-full h-24 sm:h-28 rounded-lg overflow-hidden">
            <Image
              src={isSafeNextImageSrc(service.image) as string}
              alt={typeof service.name === 'string' ? service.name : String(service.name || 'Service')}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 640px) 50vw, 50vw"
            />
          </div>
        ) : (
          <div className="relative mb-2 sm:mb-3 w-10 h-10 sm:w-12 sm:h-12 mx-auto flex-shrink-0">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <service.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white flex-shrink-0" width={24} height={24} />
            </div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300"></div>
          </div>
        )}
        
        <div className="flex items-start justify-between gap-2 mb-1 sm:mb-2">
          <h3 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors overflow-hidden text-ellipsis flex-1" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {typeof service.name === 'string' ? service.name : String(service.name || 'Service')}
          </h3>
          {!simplified && (
            <div className="flex-shrink-0 space-y-1">
              {/* Always show Instant price */}
              <div className="text-right">
                <span className="text-xs text-gray-500 dark:text-gray-400">Instant:</span>
                <div className="text-sm sm:text-base font-bold text-green-600 dark:text-green-400">
                  {service.instant_rate ? `$${service.instant_rate}` : 'Contact'}
                </div>
              </div>
              {/* Always show Scheduled price */}
              <div className="text-right">
                <span className="text-xs text-gray-500 dark:text-gray-400">Scheduled:</span>
                <div className="text-sm sm:text-base font-bold text-blue-600 dark:text-blue-400">
                  {service.scheduled_rate ? `$${service.scheduled_rate}` : 'Contact'}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {!simplified && (
          <>
            <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-2 flex-grow overflow-hidden text-ellipsis" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {typeof service.description === 'string' ? service.description : String(service.description || '')}
            </p>
            <span className="mt-auto text-[10px] sm:text-xs text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors group/link inline-flex items-center justify-center">
              View More
              <ChevronDown
                className="ml-1 w-3 h-3 sm:w-4 sm:h-4 group-hover/link:translate-y-1 transition-transform duration-300 flex-shrink-0"
                width={16}
                height={16}
              />
            </span>
          </>
        )}
      </button>
    )), [displayedServices, simplified, handleServiceClick]);

  const desktopServiceCards = useMemo(() =>
    displayedServices.map((service, index) => (
      <button
        key={index}
        type="button"
        onClick={(e) => handleServiceClick(index, service, e)}
        className={`group bg-white dark:bg-dark-800 rounded-lg ${simplified ? 'p-4' : 'p-4 sm:p-5'} shadow-brand-sm hover:shadow-brand-lg transition-all duration-300 border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-400 hover:bg-gradient-to-br hover:from-primary-50/50 hover:to-accent-50/30 dark:hover:bg-gradient-to-br dark:hover:from-primary-900/20 dark:hover:to-accent-900/20 hover:scale-105 flex flex-col ${simplified ? 'items-center text-center' : 'text-left'} cursor-pointer`}
      >
        {isSafeNextImageSrc(service.image) ? (
          <div className={`relative mb-3 w-full ${simplified ? 'h-24' : 'h-32 sm:h-40'} rounded-lg overflow-hidden`}>
            <Image
              src={isSafeNextImageSrc(service.image) as string}
              alt={typeof service.name === 'string' ? service.name : String(service.name || 'Service')}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes={simplified ? "(max-width: 1024px) 33vw, 16vw" : "(max-width: 640px) 50vw, (max-width: 768px) 33vw, (max-width: 1024px) 25vw, 20vw"}
            />
          </div>
        ) : (
          <div className={`relative mb-3 ${simplified ? 'w-16 h-16' : 'w-12 h-12 sm:w-14 sm:h-14'} ${simplified ? 'mx-auto' : ''} flex-shrink-0`}>
            <div className={`${simplified ? 'w-16 h-16' : 'w-12 h-12 sm:w-14 sm:h-14'} bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
              <service.icon className={`${simplified ? 'w-8 h-8' : 'w-6 h-6 sm:w-7 sm:h-7'} text-white flex-shrink-0`} width={simplified ? 32 : 28} height={simplified ? 32 : 28} />
            </div>
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 ${simplified ? 'w-16 h-16' : 'w-12 h-12 sm:w-14 sm:h-14'} bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300`}></div>
          </div>
        )}
        
        <div className={`flex items-start justify-between gap-3 ${simplified ? 'mb-0' : 'mb-2'}`}>
          <h3 className={`${simplified ? 'text-sm' : 'text-sm sm:text-base'} font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors overflow-hidden text-ellipsis flex-1`} style={{ display: '-webkit-box', WebkitLineClamp: simplified ? 2 : 2, WebkitBoxOrient: 'vertical' }}>
            {typeof service.name === 'string' ? service.name : String(service.name || 'Service')}
          </h3>
          {!simplified && (
            <div className="flex-shrink-0 space-y-2">
              {/* Always show Instant price */}
              <div className="text-right">
                <span className="text-xs text-gray-500 dark:text-gray-400">Instant:</span>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">
                  {service.instant_rate ? `$${service.instant_rate}` : 'Contact'}
                </div>
              </div>
              {/* Always show Scheduled price */}
              <div className="text-right">
                <span className="text-xs text-gray-500 dark:text-gray-400">Scheduled:</span>
                <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {service.scheduled_rate ? `$${service.scheduled_rate}` : 'Contact'}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {!simplified && (
          <>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3 flex-grow overflow-hidden text-ellipsis" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {typeof service.description === 'string' ? service.description : String(service.description || '')}
            </p>
            <span className="mt-auto text-xs sm:text-sm text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors group/link inline-flex items-center">
              View More
              <ChevronDown
                className="ml-1 w-4 h-4 group-hover/link:translate-y-1 transition-transform duration-300 flex-shrink-0"
                width={16}
                height={16}
              />
            </span>
          </>
        )}
      </button>
    )), [displayedServices, simplified, handleServiceClick]);
  
  // Debug: Log modal state
  useEffect(() => {
    if (showModal !== null) {
      console.log('🔍 Modal Debug:', {
        showModal,
        hasSelectedServiceData: !!selectedServiceData,
        selectedServiceName: selectedService?.name,
        selectedServiceDataName: selectedServiceData?.name,
        servicesLength: services.length
      });
    }
  }, [showModal, selectedServiceData, selectedService, services.length]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showModal !== null) {
        closeModal();
      }
    };

    if (showModal !== null) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open - smooth transition
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = '0px'; // Prevent layout shift
      });
    } else {
      // Restore scroll after transition
      const timer = setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }, 200); // Match transition duration
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showModal]);

  // Auto-open modal if selectedServiceId is provided (from URL params on services page)
  useEffect(() => {
    try {
      // Don't auto-open if modal was manually closed
      if (manuallyClosed) {
        return;
      }
      
      if (selectedServiceId && services.length > 0 && showModal === null) {
        if (usingFallback) {
          console.warn('⚠️ Using fallback services; ignore selectedServiceId to avoid mismatch');
          return;
        }
        const serviceIndex = services.findIndex(s => {
          try {
            return s.id === Number(selectedServiceId) || s.id === selectedServiceId || String(s.id) === String(selectedServiceId);
          } catch {
            return false;
          }
        });
        
        if (serviceIndex !== -1 && serviceIndex >= 0 && serviceIndex < services.length) {
          const service = services[serviceIndex];
          
          // Validate service data exists
          if (!service || !service.id) {
            console.warn('⚠️ Service data incomplete:', service);
            return;
          }
          
          // Create a copy to ensure data is ready
          const serviceCopy: ServiceCard = {
            id: service.id,
            icon: service.icon,
            name: service.name || 'Service',
            description: service.description || '',
            features: Array.isArray(service.features) ? [...service.features] : [],
            href: service.href || '/',
            category: service.category || '',
            actionLabel: service.actionLabel,
            extraDetails: service.extraDetails,
            image: service.image,
            price: service.price,
            instant_rate: service.instant_rate,
            scheduled_rate: service.scheduled_rate,
            service_type: service.service_type,
            duration_minutes: service.duration_minutes
          };
          
          // Set data first, then modal state
          setSelectedServiceData(serviceCopy);
          setIframeError(false);
          
          if (service.href && 
              service.href !== '/' && 
              validIframeRoutes.includes(service.href)) {
            setIframeUrl(service.href);
          } else {
            setIframeUrl(null);
          }
          
          setShowModal(serviceIndex);
          console.log('✅ Auto-opened modal for service ID:', selectedServiceId);
        } else {
          console.warn('⚠️ Service not found for ID:', selectedServiceId, 'Available services:', services.map(s => s.id));
        }
      }
    } catch (error) {
      console.error('❌ Error auto-opening modal:', error);
      // Don't throw - prevent error boundary from catching this
      // Just log and continue
    }
  }, [selectedServiceId, services, showModal, manuallyClosed, usingFallback]);
  
  // Reset manuallyClosed flag when selectedServiceId changes (new service clicked)
  useEffect(() => {
    if (selectedServiceId) {
      setManuallyClosed(false);
    }
  }, [selectedServiceId]);

  // Debug logging
  useEffect(() => {
    if (showModal !== null) {
      console.log('🔍 Modal state:', { showModal, selectedService: selectedService?.name, servicesCount: services.length });
    }
  }, [showModal, selectedService, services.length]);
  const getPrimaryCta = (service: ServiceCard) => {
    if (!service) {
      return {
        href: '/appointments',
        label: 'Book Appointment'
      };
    }

    // Only use service href if it's a valid navigation route
    // This prevents 404 errors from non-existent routes like /services/astrology
    if (service.href && 
        service.href !== '/' && 
        validNavigationRoutes.includes(service.href)) {
      return {
        href: service.href,
        label: service.actionLabel || 'Book Service'
      };
    }

    // For services with invalid/non-existent routes, default to appointments page
    return {
      href: '/appointments',
      label: service.actionLabel || 'Book Appointment'
    };
  };

  return (
    <>
      <section
        id="services"
        className={`bg-white dark:bg-dark-900 w-full pt-10 sm:pt-12 lg:pt-14 pb-6 sm:pb-8 lg:pb-10`}
        style={{ 
          contentVisibility: 'auto', 
          containIntrinsicSize: '900px',
          // Prevent layout shift when modal opens/closes
          willChange: 'auto'
        }}
      >
        <div className="w-full flex justify-center">
          <div className="container w-full max-w-screen-2xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6">
          <div className="text-center mb-5 sm:mb-6 pt-0 sm:pt-0 mt-0">
            {!simplified && (
              <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Astrology Services in Nepal - Vedic Jyotish & Spiritual Services
              </h1>
            )}
            <h2 className={`${simplified ? 'text-2xl' : 'text-xl'} sm:text-2xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4 mt-0 sm:mt-0`}>
              Our Spiritual & Astrological Services
            </h2>
            <p className="text-sm sm:text-base md:text-base text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Discover ancient wisdom and modern guidance through our comprehensive range of spiritual services, astrological consultations, and traditional remedies. Check our <a href="#calendar" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">daily panchanga</a> or use our <Link href="/date-converter" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">date converter</Link>.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
              {typeof error === 'string' ? error : (error as any)?.message || 'An error occurred'}
            </div>
          )}

          {displayedServices.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                {categoryFilter 
                  ? `No services found in "${categoryFilter}" category.` 
                  : 'No services available at the moment.'}
              </p>
              {categoryFilter && (
                <a
                  href="/services"
                  className="mt-4 inline-block text-primary-600 dark:text-primary-400 hover:underline"
                >
                  View all categories
                </a>
              )}
            </div>
          ) : (
            <>
              {normalizedCategoryFilter && subcategoryGroups.length > 0 && !simplified && (
                <div className="mb-6 sm:mb-8">
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3 text-left">
                    Select Sub Category
                  </h3>
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    {subcategoryGroups.map((group) => {
                      const isActive = selectedSubcategory === group.key;
                      return (
                        <button
                          key={group.key}
                          type="button"
                          onClick={() => setSelectedSubcategory(group.key)}
                          className={`px-3 sm:px-4 py-2 rounded-full border text-xs sm:text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500 dark:border-primary-500'
                              : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-400'
                          }`}
                        >
                          {group.label} ({group.count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {simplified && displayedServices.length > 0 ? (

                <>
                  {/* Auto-slide interval for carousel */}
                  {typeof window !== 'undefined' && (
                    <AutoSlideCarousel
                      carouselIndex={carouselIndex}
                      setCarouselIndex={setCarouselIndex}
                      totalSlides={Math.ceil(mobileServiceCards.length / 4)}
                      interval={5000}
                    />
                  )}
                  {/* Mobile carousel - groups of 4 */}
                  <div className="md:hidden mt-3 relative group" style={{ willChange: 'auto' }}>
                    <div className="relative overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100 dark:from-dark-800 dark:to-dark-900 rounded-2xl p-4">
                      {/* Carousel - Show 4 items at a time (2x2 grid) */}
                      <div className="flex transition-transform duration-500 ease-out" style={{
                        transform: `translateX(-${carouselIndex * 100}%)`
                      }}>
                        {Array.from({ length: Math.ceil(mobileServiceCards.length / 4) }).map((_, pageIndex) => (
                          <div key={pageIndex} className="w-full flex-shrink-0 grid grid-cols-2 gap-3 sm:gap-4">
                            {mobileServiceCards.slice(pageIndex * 4, (pageIndex + 1) * 4)}
                          </div>
                        ))}
                      </div>
                      {/* Navigation Buttons */}
                      {carouselIndex > 0 && (
                        <button
                          onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-dark-700 hover:bg-primary-500 dark:hover:bg-primary-600 text-gray-900 dark:text-white hover:text-white transition-all duration-200 rounded-full p-2 shadow-brand-md hover:shadow-brand-lg"
                          aria-label="Previous services"
                        >
                          <ChevronLeft className="w-5 h-5 flex-shrink-0" width={20} height={20} />
                        </button>
                      )}
                      {carouselIndex < Math.ceil(mobileServiceCards.length / 4) - 1 && (
                        <button
                          onClick={() => setCarouselIndex(carouselIndex + 1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-dark-700 hover:bg-primary-500 dark:hover:bg-primary-600 text-gray-900 dark:text-white hover:text-white transition-all duration-200 rounded-full p-2 shadow-brand-md hover:shadow-brand-lg"
                          aria-label="Next services"
                        >
                          <ChevronRight className="w-5 h-5 flex-shrink-0" width={20} height={20} />
                        </button>
                      )}
                      {/* Carousel indicators */}
                      {Math.ceil(mobileServiceCards.length / 4) > 1 && (
                        <div className="flex justify-center gap-2 mt-4">
                          {Array.from({ length: Math.ceil(mobileServiceCards.length / 4) }).map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCarouselIndex(index)}
                              className="p-2 flex items-center justify-center"
                              aria-label={`Go to slide ${index + 1}`}
                            >
                              <span
                                className={`block h-2 rounded-full transition-all duration-300 ${
                                  carouselIndex === index
                                    ? 'bg-primary-600 dark:bg-primary-400 w-6'
                                    : 'bg-gray-400 dark:bg-gray-600 hover:bg-gray-500 dark:hover:bg-gray-500 w-2'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Desktop carousel - groups of 4 */}
                  <div className="hidden md:block mt-3 relative group" style={{ willChange: 'auto' }}>
                    <div className="relative overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100 dark:from-dark-800 dark:to-dark-900 rounded-2xl p-4 sm:p-5">
                      {/* Carousel - Show 4 items at a time */}
                      <div className="flex transition-transform duration-500 ease-out" style={{
                        transform: `translateX(-${carouselIndex * 25}%)`
                      }}>
                        {Array.from({ length: Math.ceil(visibleServices.length / 4) }).map((_, pageIndex) => (
                          <div key={pageIndex} className="w-full flex-shrink-0 grid grid-cols-4 gap-4">
                            {desktopServiceCards.slice(pageIndex * 4, (pageIndex + 1) * 4)}
                          </div>
                        ))}
                      </div>
                      {/* Left Navigation Button */}
                      {carouselIndex > 0 && (
                        <button
                          onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-dark-700 hover:bg-primary-500 dark:hover:bg-primary-600 text-gray-900 dark:text-white hover:text-white transition-all duration-200 rounded-full p-3 shadow-brand-md hover:shadow-brand-lg"
                          aria-label="Previous services"
                        >
                          <ChevronLeft className="w-6 h-6 flex-shrink-0" width={24} height={24} />
                        </button>
                      )}
                      {/* Right Navigation Button */}
                      {carouselIndex < Math.ceil(displayedServices.length / 4) - 1 && (
                        <button
                          onClick={() => setCarouselIndex(carouselIndex + 1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-dark-700 hover:bg-primary-500 dark:hover:bg-primary-600 text-gray-900 dark:text-white hover:text-white transition-all duration-200 rounded-full p-3 shadow-brand-md hover:shadow-brand-lg"
                          aria-label="Next services"
                        >
                          <ChevronRight className="w-6 h-6 flex-shrink-0" width={24} height={24} />
                        </button>
                      )}
                      {/* Carousel indicators (dots) */}
                      {displayedServices.length > 4 && (
                        <div className="flex justify-center gap-2 mt-4">
                          {Array.from({ length: Math.ceil(displayedServices.length / 4) }).map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCarouselIndex(index)}
                              className="p-2 flex items-center justify-center"
                              aria-label={`Go to slide ${index + 1}`}
                            >
                              <span
                                className={`block h-2 rounded-full transition-all duration-300 ${
                                  carouselIndex === index
                                    ? 'bg-primary-600 dark:bg-primary-400 w-6'
                                    : 'bg-gray-400 dark:bg-gray-600 hover:bg-gray-500 dark:hover:bg-gray-500 w-2'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Mobile-friendly grid layout - 2 columns (for services page) */}
                  <div className="md:hidden mt-6 flex justify-center" style={{ willChange: 'auto' }}>
                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 w-full">
                      {mobileServiceCards}
                    </div>
                  </div>

                  {/* Tablet grid - 2 columns centered */}
                  <div className="hidden md:flex lg:hidden mt-6 justify-center" style={{ willChange: 'auto' }}>
                    <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-2xl">
                      {desktopServiceCards}
                    </div>
                  </div>

                  {/* Desktop grid - 3+ columns */}
                  <div className={`hidden lg:grid grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 w-full mb-6`} style={{ willChange: 'auto' }}>
                    {desktopServiceCards}
                  </div>
                </>
              )}

            </>
          )}
          {loading && (
            <div className="mt-6 text-center text-sm text-gray-500">
              Loading services...
            </div>
          )}
          </div>
        </div>
      </section>

      {/* Service Detail Modal - Always rendered but hidden with CSS for smooth transitions */}
      {/* Render modal always but use CSS for show/hide to prevent flashing */}
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto transition-opacity duration-200 ease-in-out ${
          showModal !== null && selectedServiceData 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        } ${isModalTransitioning ? 'transitioning' : ''}`}
        onClick={closeModal}
        style={{
          backgroundColor: showModal !== null ? 'rgba(0, 0, 0, 0.6)' : 'transparent',
          transition: 'opacity 200ms ease-in-out, background-color 200ms ease-in-out',
          willChange: 'opacity, background-color'
        }}
        aria-hidden={showModal === null}
      >
        {selectedServiceData && (
          <div 
            className={`bg-white dark:bg-dark-800 rounded-xl shadow-brand-lg w-full max-w-2xl md:max-w-4xl lg:max-w-5xl p-0 relative max-h-[90vh] md:max-h-[85vh] overflow-hidden flex flex-col my-auto transition-all duration-200 ease-in-out ${
              showModal !== null 
                ? 'opacity-100 scale-100 translate-y-0' 
                : 'opacity-0 scale-95 translate-y-4'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{
              willChange: 'opacity, transform'
            }}
          >
            {/* Header with Close Button */}
            <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-900">
                <div className="flex items-center gap-3">
              {isSafeNextImageSrc(selectedServiceData?.image) ? (
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                    <Image
                    src={isSafeNextImageSrc(selectedServiceData?.image) as string}
                      alt={selectedServiceData?.name || 'Service'}
                      fill
                      className="object-cover"
                      sizes="40px"
                    />
                  </div>
                ) : selectedServiceData?.icon ? (
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <selectedServiceData.icon className="w-5 h-5 text-white flex-shrink-0" width={20} height={20} />
                  </div>
                ) : null}
                <div>
                  <h2 className="text-base sm:text-lg md:text-lg font-bold text-gray-900 dark:text-white">
                    {typeof selectedServiceData.name === 'string' ? selectedServiceData.name : String(selectedServiceData.name || 'Service')}
                  </h2>
                  {selectedServiceData.price && (
                    <div className="mt-1 space-y-1">
                      <div className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                        Base: {typeof selectedServiceData.price === 'string' ? selectedServiceData.price : String(selectedServiceData.price || '')}
                      </div>
                      <div className="text-xs font-medium text-green-600 dark:text-green-400">
                        Instant: {(selectedServiceData as any).instant_rate ? `$${(selectedServiceData as any).instant_rate}` : 'Contact for pricing'}
                      </div>
                      <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        Scheduled: {(selectedServiceData as any).scheduled_rate ? `$${(selectedServiceData as any).scheduled_rate}` : 'Contact for pricing'}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10 p-2 hover:bg-gray-200 dark:hover:bg-dark-700 rounded-lg"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 flex-shrink-0" width={24} height={24} />
              </button>
            </div>

            {/* Content Area - Iframe or Service Details */}
            <div className="flex-1 overflow-hidden">
              {iframeUrl && !iframeError ? (
                <iframe
                  src={iframeUrl}
                  className="w-full h-full min-h-[400px] md:min-h-[500px] border-0"
                  title={typeof selectedServiceData?.name === 'string' ? selectedServiceData.name : String(selectedServiceData?.name || 'Service')}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                  onError={handleIframeError}
                />
              ) : (
                <div className="p-4 sm:p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-150px)] md:max-h-[calc(85vh-150px)]">
                  {/* Service Image */}
                  {isSafeNextImageSrc(selectedServiceData.image) && (
                    <div className="mb-6">
                      <div className="relative w-full h-48 rounded-lg overflow-hidden">
                        <Image
                          src={isSafeNextImageSrc(selectedServiceData.image) as string}
                          alt={selectedServiceData?.name || 'Service'}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, 768px"
                        />
                      </div>
                    </div>
                  )}
                  {/* Pricing Information */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/30 dark:to-accent-900/30 rounded-lg border border-primary-200 dark:border-primary-800">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
                      Pricing
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Always show Instant Rate */}
                      <div className="p-3 bg-white dark:bg-dark-700 rounded-lg border-l-4 border-green-500">
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Instant Rate</p>
                        <p className="text-lg sm:text-xl font-bold text-green-600 dark:text-green-400">
                          {(selectedServiceData as any).instant_rate ? `$${(selectedServiceData as any).instant_rate}` : 'Contact'}
                        </p>
                      </div>
                      {/* Always show Scheduled Rate */}
                      <div className="p-3 bg-white dark:bg-dark-700 rounded-lg border-l-4 border-blue-500">
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Scheduled Rate</p>
                        <p className="text-lg sm:text-xl font-bold text-blue-600 dark:text-blue-400">
                          {(selectedServiceData as any).scheduled_rate ? `$${(selectedServiceData as any).scheduled_rate}` : 'Contact'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Service Type & Availability */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-dark-700 dark:to-dark-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
                      Service Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-3 bg-white dark:bg-dark-900 rounded-lg">
                        <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Service Type</p>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            (selectedServiceData as any).service_type === 'instant'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          }`}>
                            {(selectedServiceData as any).service_type === 'instant' ? 'Instant' : 'Scheduled'}
                          </span>
                        </div>
                      </div>
                      {(selectedServiceData as any).duration_minutes && (
                        <div className="p-3 bg-white dark:bg-dark-900 rounded-lg">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">Duration</p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            {(selectedServiceData as any).duration_minutes} minutes
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* All Features */}
                  {selectedServiceData?.features && Array.isArray(selectedServiceData.features) && selectedServiceData.features.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
                        Service Features
                      </h3>
                      <ul className="space-y-3">
                        {selectedServiceData.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start text-gray-700 dark:text-gray-300">
                            <span className="text-primary-600 dark:text-primary-400 mr-3 font-bold flex-shrink-0 mt-1">✓</span>
                            <span className="text-base">{typeof feature === 'string' ? feature : String(feature || '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Category */}
                  {selectedServiceData?.category && (
                    <div className="mb-6">
                      <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium">
                        {typeof selectedServiceData.category === 'string' ? selectedServiceData.category : String(selectedServiceData.category || '')}
                      </span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowBookingForm(true)}
                      className="flex-1 btn btn-primary text-center"
                    >
                      {selectedServiceData?.price ? 'Book Now' : 'Order Service'}
                    </button>
                    <button
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Service Booking Form Modal */}
      {showBookingForm && selectedServiceData && (
        <ServiceBookingForm
          service={{
            id: selectedServiceData.id,
            name: selectedServiceData.name,
            price: selectedServiceData.price,
            formFields: (selectedServiceData as any).formFields || [],
            serviceTypes: (selectedServiceData as any).serviceTypes || []
          }}
          onClose={() => setShowBookingForm(false)}
          onSuccess={() => {
            setShowBookingForm(false);
            closeModal();
          }}
        />
      )}
    </>
  );
}

